/**
 * Installer auth store (Pinia).
 *
 * Holds the new-app installer session for the two personas (FCM / Contract
 * Worker — see INSTALLER_SURVEY_MERGE_PLAN.md §4, §5). The opaque session token
 * is persisted to @capacitor/preferences (`wattr.installer.session`) so the
 * installerApi request interceptor can attach it as `Authorization: Bearer`
 * and so the session survives app restarts.
 *
 * NEVER persists the installer password — re-auth on 401 instead. Any 401 from
 * installerApi clears the session and drops persona to null; the login page /
 * router guard sends the user back to the login screen.
 */
import { defineStore } from 'pinia';
import { Preferences } from '@capacitor/preferences';
import axios from 'axios';
import {
  installerHttp,
  INSTALLER_SESSION_KEY,
  loginFcm,
  loginContractPhone,
  logout as apiLogout,
  getMe,
  type InstallerPersona,
} from '../services/installerApi';

interface InstallerAuthState {
  persona: InstallerPersona | null;
  sessionToken: string | null;
  clientFid: number | null;
  phone: string | null;
  loading: boolean;
  error: string | null;
}

/** Module-scoped guard so the 401 interceptor is wired exactly once. */
let interceptorInstalled = false;

export const useInstallerAuthStore = defineStore('installerAuth', {
  state: (): InstallerAuthState => ({
    persona: null,
    sessionToken: null,
    clientFid: null,
    phone: null,
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (s): boolean => !!s.sessionToken && !!s.persona,
    isFcm: (s): boolean => s.persona === 'fcm',
    isContract: (s): boolean => s.persona === 'contract',
  },

  actions: {
    /** Persist (or clear) the session token in Preferences. */
    async _persistToken(token: string | null): Promise<void> {
      if (token) {
        await Preferences.set({ key: INSTALLER_SESSION_KEY, value: token });
      } else {
        await Preferences.remove({ key: INSTALLER_SESSION_KEY });
      }
    },

    /** Clear all session state + storage. Called on logout and on any 401. */
    async _clearSession(): Promise<void> {
      this.persona = null;
      this.sessionToken = null;
      this.clientFid = null;
      this.phone = null;
      await this._persistToken(null);
    },

    /** Install the 401 → clear-session response interceptor once. */
    _ensureInterceptor(): void {
      if (interceptorInstalled) return;
      interceptorInstalled = true;
      installerHttp.interceptors.response.use(
        (res) => res,
        (err) => {
          if (axios.isAxiosError(err) && err.response?.status === 401) {
            // Fire-and-forget — clear the session so subsequent navigation
            // bounces to login. We don't await inside the interceptor.
            void this._clearSession();
          }
          return Promise.reject(err);
        },
      );
    },

    /** Load a persisted session on boot. Returns true if a token was found. */
    async restore(): Promise<boolean> {
      this._ensureInterceptor();
      const { value } = await Preferences.get({ key: INSTALLER_SESSION_KEY });
      if (!value) return false;
      this.sessionToken = value;
      try {
        // Validate + hydrate persona/identity from the server.
        const me = await getMe();
        this.persona = me.persona;
        this.clientFid = me.clientFid ?? null;
        this.phone = me.phone ?? null;
        return true;
      } catch (err) {
        // Token rejected/expired → the 401 interceptor already cleared it.
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          return false;
        }
        // Network error on boot — keep the token, persona stays null until a
        // later call succeeds. Treat as "not yet authenticated".
        return false;
      }
    },

    /** FCM login with legacy username/password. */
    async loginFcm(username: string, password: string): Promise<boolean> {
      this._ensureInterceptor();
      this.loading = true;
      this.error = null;
      try {
        const res = await loginFcm(username, password);
        this.sessionToken = res.sessionToken;
        this.persona = res.persona;
        this.clientFid = res.clientFid ?? null;
        this.phone = res.phone ?? null;
        await this._persistToken(res.sessionToken);
        return true;
      } catch (err) {
        this.error = extractError(err);
        await this._clearSession();
        return false;
      } finally {
        this.loading = false;
      }
    },

    /** Contract worker login with phone number only. */
    async loginContractPhone(phone: string): Promise<boolean> {
      this._ensureInterceptor();
      this.loading = true;
      this.error = null;
      try {
        const res = await loginContractPhone(phone);
        this.sessionToken = res.sessionToken;
        this.persona = res.persona;
        this.clientFid = res.clientFid ?? null;
        this.phone = res.phone ?? phone;
        await this._persistToken(res.sessionToken);
        return true;
      } catch (err) {
        this.error = extractError(err);
        await this._clearSession();
        return false;
      } finally {
        this.loading = false;
      }
    },

    /** Drop the server session then clear local state. */
    async logout(): Promise<void> {
      try {
        if (this.sessionToken) await apiLogout();
      } catch {
        // Best-effort — clear locally regardless of the server response.
      } finally {
        await this._clearSession();
      }
    },

    /** Re-fetch persona/identity from the server (GET /me). */
    async me(): Promise<void> {
      const m = await getMe();
      this.persona = m.persona;
      this.clientFid = m.clientFid ?? null;
      this.phone = m.phone ?? null;
    },
  },
});

/** Pull a human-readable message off an axios error, else a generic fallback. */
function extractError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { message?: string; error?: string } | undefined;
    if (body?.message) return body.message;
    if (body?.error) return body.error;
    if (err.response?.status === 401) return 'Invalid credentials.';
    if (!err.response) return 'Network error — check your connection.';
  }
  return 'Something went wrong. Please try again.';
}
